import {
  Button,
  Loader,
  PopupEventWrapper,
  Sidebar,
  SidebarFooter,
  SidebarHeader,
  TabForm,
} from 'components';
import { ContentSlot, DialogTextArea } from 'components/PopupEvent/styled';
import { CreateEventStates } from 'constants/index';
import { EditEventType } from 'constants/sidebars';
import { useClickOutsideClose } from 'hooks';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import { doGetCommunityMembersByGroups } from 'store/actionCreators/currentCommunity';
import { closeModal } from 'store/actionCreators/modal';
import { getCurrentCommunity } from 'store/selectors/currentCommunity';
import { getCreateEventLoading } from 'store/selectors/events';
import { getData } from 'store/selectors/modal';
import { breakpoints } from 'theme';
import { CreateEventSidebarData } from 'types';
import { getUpdatedFields, isNotifyPopupDisabled } from 'utils';

import DocumentsTab from './Documents';
import EventTab from './Event';
import { useSubmitMainInfo } from './hooks';
import ParticipantsTab from './Participants';
import TasksTab from './Tasks';
import { getEventRequestBody } from './utils';

const CreateEventOverall = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDownMd = useMedia(breakpoints.downMd);
  const [notifyPopup, setNotifyPopup] = useState<{
    isOpen: boolean;
    type?: EditEventType;
    notifyText?: string;
    notify?: boolean;
  }>({
    isOpen: false,
  });
  const community = useSelector(getCurrentCommunity);
  const isLoading = useSelector(getCreateEventLoading);
  const modalData = useSelector(getData) as CreateEventSidebarData;
  const {
    filterFrom,
    filterTo,
    currentViewFrom,
    currentViewTo,
    event,
    groupId,
    openTasksTab,
  } = modalData;
  const refModal = React.createRef<HTMLDivElement>();

  const sidebarState: CreateEventStates = event
    ? CreateEventStates.EVENT_UPDATE
    : CreateEventStates.EVENT_CREATE;
  const sidebarLabel =
    sidebarState === CreateEventStates.EVENT_CREATE
      ? t('event.createEvent')
      : t('event.updateEvent');

  useEffect(() => {
    if (!groupId) {
      dispatch(
        doGetCommunityMembersByGroups({
          communityId: community.id,
          withoutBlocked: true,
        }),
      );
    }
  }, []);

  const closeSidebar = () => dispatch(closeModal());

  const {
    onSubmit,
    register,
    trigger,
    errors,
    getValues,
    recipientsFieldProps,
    eventResponseFieldProps,
    tasksFieldProps,
    documentsFieldProps,
    weekDaysFieldProps,
    control,
    watchedValues,
    setValue,
  } = useSubmitMainInfo({
    event,
    closeSidebar,
    filterFrom,
    filterTo,
    currentViewFrom,
    currentViewTo,
    sidebarState,
    communityId: community.id,
    groupId,
    community,
  });

  const handleTabChange = async () => {
    const isValid = await trigger();

    if (!isValid) {
      throw new Error();
    }
  };

  const onSubmitClick = async (type: EditEventType) => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    const newValues = getValues();
    const updatedFields = getUpdatedFields(
      event,
      getEventRequestBody(newValues),
    );

    const ignoreNotifyPopup = isNotifyPopupDisabled(
      sidebarState,
      event,
      community.notifications,
      updatedFields,
    );

    if (ignoreNotifyPopup) {
      return onSubmit(type, { toNotify: false, notifyText: '' });
    }

    setNotifyPopup({ isOpen: true, type });
  };

  const editSelectionOptions = [
    {
      value: EditEventType.SINGLE_EVENT,
      label: t('event.eventOperations.eventOnly'),
      handleConfirm: () => onSubmitClick(EditEventType.SINGLE_EVENT),
    },
    {
      value: EditEventType.SINGLE_AND_FOLLOW,
      label: t('event.eventOperations.eventAndFollowUp'),
      handleConfirm: () => onSubmitClick(EditEventType.SINGLE_AND_FOLLOW),
    },
    {
      value: EditEventType.FULL_EVENT,
      label: t('event.eventOperations.all'),
      handleConfirm: () => onSubmitClick(EditEventType.FULL_EVENT),
    },
  ];

  const popupButtons = [
    {
      popupButtonText: t('communitySettings.notificationPopupBack'),
      variant: 'text',
      handleConfirm: () => setNotifyPopup({ isOpen: false }),
    },
    {
      popupButtonText: t('communitySettings.notificationPopupDoNotNotify'),
      variant: 'text',
      handleConfirm: () =>
        onSubmit(notifyPopup.type, { toNotify: false, notifyText: '' }),
    },
    {
      popupButtonText: t('communitySettings.notificationPopupNotify'),
      handleConfirm: () =>
        onSubmit(notifyPopup.type, {
          toNotify: true,
          notifyText: notifyPopup.notifyText,
        }),
    },
  ];

  useClickOutsideClose({ ref: refModal, callback: closeSidebar });

  const buttonContent = isLoading ? (
    <Loader type="button" size="28px" thickness={2} />
  ) : (
    sidebarLabel
  );

  const buttonVariant =
    sidebarState === CreateEventStates.EVENT_CREATE ? 'primary' : 'secondary';

  return (
    <Sidebar ref={refModal}>
      <SidebarHeader label={sidebarLabel} onCloseClick={closeSidebar} />
      {notifyPopup.isOpen && (
        <PopupEventWrapper
          innerComponent={
            <ContentSlot>
              <DialogTextArea
                value={notifyPopup.notifyText}
                placeholder={t(
                  'communitySettings.notificationPopupMessageLabel',
                )}
                rows={4}
                onChange={(e) =>
                  setNotifyPopup({
                    ...notifyPopup,
                    notifyText: e.target.value,
                  })
                }
              />
            </ContentSlot>
          }
          isOpen={true}
          popupMessageText={t('communitySettings.notificationPopupHeader')}
          popupButtons={popupButtons}
          enhancedLayout
          stackFooterActions={isDownMd}
          popupStyles={{
            width: isDownMd ? '94%' : '520px',
            maxWidth: '520px',
          }}
          darkBackground={true}
          onPopupClose={() => setNotifyPopup({ isOpen: false })}
        />
      )}
      <form>
        <TabForm
          // width="100%"
          isSidebar
          initialActiveTab={openTasksTab ? 2 : 0}
        >
          <div
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            label={t('event.event')}
            withSubmit
            submitForm={handleTabChange}
            index={0}
          >
            <EventTab
              eventResponseId={event?.defaultEventResponseId}
              editMode={Boolean(
                sidebarState === CreateEventStates.EVENT_UPDATE,
              )}
              register={register}
              errors={errors}
              sidebarState={sidebarState}
              control={control}
              watchedValues={watchedValues}
              weekDaysFieldProps={weekDaysFieldProps}
              setValue={setValue}
              eventResponseFieldProps={eventResponseFieldProps}
            />
          </div>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <div label={t('common.participants')} index={1}>
            <ParticipantsTab
              recipientsFieldProps={recipientsFieldProps}
              groupId={groupId}
            />
          </div>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <div label={t('common.tasks')} index={2}>
            <TasksTab tasksFieldProps={tasksFieldProps} />
          </div>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <div label={t('common.documentation')} iconLabel grow index={3}>
            <DocumentsTab
              documentsFieldProps={documentsFieldProps}
              groupId={groupId}
            />
          </div>
        </TabForm>
        <SidebarFooter>
          {sidebarState === CreateEventStates.EVENT_UPDATE &&
          event?.eventSeriesId ? (
            <PopupEventWrapper
              selectionOptions={editSelectionOptions}
              defaultSelectionValue={EditEventType.SINGLE_EVENT}
              popupMessageText={t('event.eventOperations.editText')}
              darkBackground={true}
            >
              <Button
                type="button"
                variant={buttonVariant}
                disabled={isLoading}
              >
                {buttonContent}
              </Button>
            </PopupEventWrapper>
          ) : (
            <Button
              type="button"
              variant={buttonVariant}
              disabled={isLoading}
              onClick={() => onSubmitClick(EditEventType.SINGLE_EVENT)}
            >
              {buttonContent}
            </Button>
          )}
        </SidebarFooter>
      </form>
    </Sidebar>
  );
};

export default CreateEventOverall;
